import { useTranslation } from 'react-i18next'
import { ShieldAlert } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useMemberEntry } from '@/lib/hooks/useMemberEntry'
import { SUPPORT_EMAIL } from '@/lib/memberEntry'

interface SuspendedNoticeProps {
  /** What is blocked here: people coming in, or a new sale. */
  kind: 'entry' | 'sale'
  className?: string
}

/**
 * F-86: why an action is disabled while the community is suspended. The
 * leadership (the server sent the reason) reads the SuperAdmin's reason and
 * the support contact; anyone else, the generic sentence. Renders nothing
 * when the community is not suspended.
 */
export function SuspendedNotice({ kind, className }: SuspendedNoticeProps) {
  const { t } = useTranslation('common')
  const { suspended, reason } = useMemberEntry()
  if (!suspended) return null

  if (!reason) {
    return (
      <div role="status" data-testid="member-entry-notice" className={cn('flex gap-2 rounded-lg bg-gray-100 p-3 text-sm text-gray-700', className)}>
        <ShieldAlert className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-500" aria-hidden="true" />
        <p>{t(kind === 'sale' ? 'memberEntry.salesClosed' : 'memberEntry.entryClosed')}</p>
      </div>
    )
  }

  return (
    <div role="alert" data-testid="member-entry-notice" className={cn('flex gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-900', className)}>
      <ShieldAlert className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-600" aria-hidden="true" />
      <div className="min-w-0 space-y-1">
        <p className="font-semibold">{t('memberEntry.bannerTitle')}</p>
        <p>{t(kind === 'sale' ? 'memberEntry.saleBlocked' : 'memberEntry.entryBlocked')}</p>
        <p className="line-clamp-4 whitespace-pre-line break-words text-red-800">
          <span className="font-medium">{t('memberEntry.reasonLabel')}:</span> {reason}
        </p>
        <p className="text-xs text-red-700">
          {t('memberEntry.support')}{' '}
          <a href={`mailto:${SUPPORT_EMAIL}`} className="font-semibold underline underline-offset-2">{SUPPORT_EMAIL}</a>
        </p>
      </div>
    </div>
  )
}
